/**
 * Visual styling per ArchiMate relation type. Each entry describes the
 * stroke (color, width, dash pattern) and the marker shapes for the
 * source / target ends. Composition + Aggregation use custom SVG markers
 * defined in `EdgeMarkers.tsx`; the rest use React Flow's built-in
 * Arrow / ArrowClosed.
 */

import type { Edge, EdgeMarker } from "@xyflow/react";
import { MarkerType } from "@xyflow/react";

export interface RelationStyle {
  style: React.CSSProperties;
  markerStart?: EdgeMarker | string | undefined;
  markerEnd?: EdgeMarker | string | undefined;
  /** Used by the canvas to set hover/selection coloring. */
  baseColor: string;
}

const C_SOLID = "#475569"; // slate-600
const C_DASHED = "#64748b"; // slate-500
const C_COMPOSITION = "#1e293b"; // slate-800
const C_REALIZATION = "#0e7490"; // cyan-700
const C_FLOW = "#7c2d12"; // amber-900
const C_ASSOC = "#94a3b8"; // slate-400

const filledArrow: EdgeMarker = {
  type: MarkerType.ArrowClosed,
  width: 18,
  height: 18,
};

const openArrow: EdgeMarker = {
  type: MarkerType.Arrow,
  width: 18,
  height: 18,
};

// Stroke patterns from the ArchiMate 3.2 spec ("Relationships and Relationship
// Connectors", N221, page reproduced in the project chat). Composition and
// Aggregation render with a diamond at the source and *no* arrow at the
// target — the line just ends at the part. Realization and Access render
// with a *dotted* line (small dots, not the longer dashes used for
// Flow/Influence). Serving and Triggering use a closed (filled) arrow;
// Specialization uses an open (hollow) triangle.
const DOTTED = "1.5 3"; // small dots, used for Realization + Access
const DASHED = "6 4"; // longer dashes, used for Flow + Influence
const RELATION_STYLES: Record<string, RelationStyle> = {
  // Custom-marker fields are passed as the bare marker id (e.g.
  // "ae-composition"); React Flow wraps them in `url('#...')` itself when
  // it renders the SVG path. Passing a pre-wrapped `url(#...)` results in
  // a double-wrap that the browser can't resolve, so the marker silently
  // disappears.
  "archimate.Composition": {
    // Filled diamond at TARGET (the whole); line starts at the source (part)
    // without a marker. Convention in this app: drag from part → whole.
    style: { strokeWidth: 1.6, stroke: C_COMPOSITION },
    markerEnd: "ae-composition",
    baseColor: C_COMPOSITION,
  },
  "archimate.Aggregation": {
    // Open diamond at TARGET (the whole); line starts at the source (part).
    style: { strokeWidth: 1.4, stroke: C_COMPOSITION },
    markerEnd: "ae-aggregation",
    baseColor: C_COMPOSITION,
  },
  "archimate.Assignment": {
    style: { strokeWidth: 1.6, stroke: C_SOLID },
    // ArchiMate spec: filled circle at SOURCE + filled arrow at TARGET.
    markerStart: "ae-assignment-dot",
    markerEnd: filledArrow,
    baseColor: C_SOLID,
  },
  "archimate.Realization": {
    // Spec: dotted line + open (hollow) triangle at target.
    style: { strokeWidth: 1.4, stroke: C_REALIZATION, strokeDasharray: DOTTED },
    markerEnd: "ae-open-triangle",
    baseColor: C_REALIZATION,
  },
  "archimate.Serving": {
    // Spec: solid line + thin (open) arrow head at target. Flow is the
    // only relation that uses the filled-triangle arrow.
    style: { strokeWidth: 1.4, stroke: C_SOLID },
    markerEnd: openArrow,
    baseColor: C_SOLID,
  },
  "archimate.Access": {
    // Spec: dotted line. Arrow heads are decided by the `access_kind`
    // attribute via attributeOverrides() — none / source / target / both.
    style: { strokeWidth: 1.2, stroke: C_DASHED, strokeDasharray: DOTTED },
    baseColor: C_DASHED,
  },
  "archimate.Triggering": {
    // Spec: solid line + filled (closed) arrow at target.
    style: { strokeWidth: 1.6, stroke: C_FLOW },
    markerEnd: filledArrow,
    baseColor: C_FLOW,
  },
  "archimate.Flow": {
    // Spec: dashed line + filled arrow at target.
    style: { strokeWidth: 1.4, stroke: C_FLOW, strokeDasharray: DASHED },
    markerEnd: filledArrow,
    baseColor: C_FLOW,
  },
  "archimate.Influence": {
    // Spec: dashed line + open arrow at target. The +/- value is rendered
    // as part of the edge label by the canvas, not the marker.
    style: { strokeWidth: 1.3, stroke: C_DASHED, strokeDasharray: DASHED },
    markerEnd: openArrow,
    baseColor: C_DASHED,
  },
  "archimate.Specialization": {
    // Spec: solid line + open (hollow) triangle at target.
    style: { strokeWidth: 1.4, stroke: C_SOLID },
    markerEnd: "ae-open-triangle",
    baseColor: C_SOLID,
  },
  "archimate.Association": {
    // Spec: solid line, no arrow (undirected).
    style: { strokeWidth: 1, stroke: C_ASSOC },
    baseColor: C_ASSOC,
  },
  "c4.Uses": {
    // Generieke C4-runtime-relatie ("uses"), inherent gericht. Basis = volle
    // lijn + gevulde pijl aan target zodat 'ie OOK op niet-C4-diagrammen
    // (bv. roadmap/I&M) een richting toont. Op c4.container/component/dynamic/
    // deployment upgrade't attributeOverrides() dit naar de dashed Flow-stijl.
    style: { strokeWidth: 1.4, stroke: C_SOLID },
    markerEnd: filledArrow,
    baseColor: C_SOLID,
  },
  // Drawing primitives — dunne grijze lijn, geen ArchiMate-semantiek.
  // De gebruiker kan stijl en kleur via de toolbar overschrijven; default
  // is een rustig grijs zodat de lijn niet als semantische relatie
  // gelezen wordt.
  "drawing.Line": {
    style: { strokeWidth: 1.2, stroke: C_ASSOC },
    baseColor: C_ASSOC,
  },
  "drawing.ArrowLine": {
    style: { strokeWidth: 1.2, stroke: C_ASSOC },
    markerEnd: openArrow,
    baseColor: C_ASSOC,
  },
};

const FALLBACK_STYLE: RelationStyle = {
  style: { strokeWidth: 1, stroke: C_ASSOC },
  baseColor: C_ASSOC,
};

export function styleForRelationType(typeId: string): RelationStyle {
  return RELATION_STYLES[typeId] ?? RELATION_STYLES["archimate.Association"] ?? FALLBACK_STYLE;
}

/**
 * Crow's-foot-marker-id voor een Sharp-cardinality enum-waarde. Zie
 * EdgeMarkers.tsx voor de definities; mapping is 1-op-1 met de enum
 * uit registry/sharp-pack.json.
 */
function crowsFootMarkerFor(cardinality: unknown): string | undefined {
  if (cardinality === "one") return "ae-cf-one";
  if (cardinality === "zero_or_one") return "ae-cf-zero-or-one";
  if (cardinality === "one_or_many") return "ae-cf-one-or-many";
  if (cardinality === "zero_or_many") return "ae-cf-zero-or-many";
  return undefined;
}

/**
 * Some relation types vary their marker shape based on attributes.
 * For Access the `access_kind` enum (read / write / read-write) decides
 * whether arrows appear at the source, target, both, or neither.
 */
/**
 * View-kinds waarop Sharp-cardinaliteits-symbolen NIET op de lijn
 * worden gerenderd. Sharp's Contextual View (Level 1) is bewust
 * cardinaliteit-vrij — het is voor stakeholder-gesprekken op het
 * conceptuele niveau waar multipliciteit afleidt van de boodschap.
 * Concept Model View (L2) en Detailed Concept View (L3) tonen
 * cardinaliteit wél. Zie archimate-sharp-extensie.md §8.10 +
 * dom-doc §7.4.
 */
const CARDINALITY_SUPPRESSED_VIEW_KINDS = new Set<string>(["sharp.contextual"]);

function attributeOverrides(
  typeId: string,
  attributes: Record<string, unknown> | undefined,
  stereotypes: readonly string[] | undefined,
  viewKind: string | undefined,
): Partial<RelationStyle> {
  if (typeId === "archimate.Flow") {
    // C4-conventie: Flow is de default-relatie tussen objecten op een
    // C4-diagram (zie view_kind.default_relation_type). Op die views
    // rendert 'ie donkergrijs i.p.v. de ArchiMate-default amber-bruin —
    // Simon Brown's render-stijl is een neutrale lijn met richting.
    if (viewKind?.startsWith("c4.")) {
      return {
        style: { strokeWidth: 1.4, stroke: "#334155", strokeDasharray: "6 4" },
      };
    }
  }
  if (typeId === "c4.Uses") {
    // c4.Uses is de generieke C4-runtime-relatie. Op container /
    // component / dynamic / deployment-views rendert 'ie als visuele
    // Flow (donkergrijs + dashed + filledArrow) zodat AppComp ↔
    // Node/SystemSoftware en Node ↔ Node consistent met Flow tussen
    // ApplicationComponents lezen — ArchiMate-matrix verbiedt Flow
    // tussen die typen, maar visueel hoort 't één type lijn te zijn.
    if (
      viewKind === "c4.container" ||
      viewKind === "c4.component" ||
      viewKind === "c4.dynamic" ||
      viewKind === "c4.deployment"
    ) {
      return {
        style: { strokeWidth: 1.4, stroke: "#334155", strokeDasharray: "6 4" },
        markerEnd: filledArrow,
      };
    }
  }
  if (typeId === "archimate.Access") {
    // «Access CRUD»-stereotype: standaard ArchiMate-Access-lijnstijl
    // (slate-grijs, gestippeld) blijft overeind — CRUD-info reist
    // via de gekleurde letter-badge in Canvas.tsx. Pijlpunten worden
    // hier bepaald door `crud_mode`: read = source-pijl (data → proces),
    // create/update/delete = target-pijl (proces → data),
    // read_write = beide. Wint van het ArchiMate-`access_kind`-pad
    // omdat het stereotype meer specifieke semantiek draagt.
    if (stereotypes?.includes("sharp.AccessCRUD")) {
      const mode = attributes?.crud_mode;
      if (mode === "read") {
        return { markerStart: openArrow, markerEnd: undefined };
      }
      if (mode === "create" || mode === "update" || mode === "delete") {
        return { markerStart: undefined, markerEnd: openArrow };
      }
      if (mode === "read_write") {
        return { markerStart: openArrow, markerEnd: openArrow };
      }
      // Onbekende / lege mode → geen markers, val terug op stippellijn.
      return { markerStart: undefined, markerEnd: undefined };
    }
    const kind = attributes?.access_kind;
    // Per ArchiMate: source = behavior/active, target = passive (data).
    // - "write" : behavior → data (arrow at target)
    // - "read"  : data → behavior (arrow at source)
    // - "read-write": arrows on both ends
    // - undefined or anything else: no arrows (plain dotted line)
    if (kind === "read") {
      return { markerStart: openArrow, markerEnd: undefined };
    }
    if (kind === "write") {
      return { markerStart: undefined, markerEnd: openArrow };
    }
    if (kind === "read-write") {
      return { markerStart: openArrow, markerEnd: openArrow };
    }
    return { markerStart: undefined, markerEnd: undefined };
  }
  if (typeId === "archimate.Association") {
    // «bmc.Realization»-stereotype rendert op BMC-views als open
    // driehoek aan target-zijde (Realization-stijl) — drager-relatie
    // is Association omdat directe archimate.Realization voor de
    // meeste BMC-bridging-paren matrix-rejected is. Op standaard
    // ArchiMate-views vervalt de Realization-stijl en blijft 't een
    // gewone gerichte Association met halve pijlpunt (semantisch
    // neutraal in die context, geen verwarring met canonical
    // Realization-pijlen).
    //
    // Implied directedness: het stereotype impliceert directedness
    // onafhankelijk van `attributes.directed`. Een Association met
    // bmc.Realization is per definitie gericht (source = realiserend
    // element → target = gerealiseerde BMC-strategie); de renderer
    // overruled hier de `directed`-attribuut-route. Zie spec §10.12.
    if (stereotypes?.includes("bmc.Realization")) {
      if (viewKind?.startsWith("bmc.")) {
        return {
          style: { strokeWidth: 1.3, stroke: C_SOLID },
          markerEnd: "ae-open-triangle",
        };
      }
      // Buiten BMC-views: half-arrow forced (directed Association),
      // ongeacht attributes.directed — het stereotype is leidend.
      return { markerEnd: "ae-half-arrow" };
    }
    // Sharp ConceptAssociation / IdentifyingAssociation — render
    // crow's-foot-symbolen aan beide eindes op basis van de
    // source_cardinality / target_cardinality enum-waarden. Gaat
    // VOOR de directed-half-arrow want Sharp-Associations zijn
    // semantisch ongericht (de cardinaliteit-symbolen vervangen
    // de pijl).
    const isSharpAssoc =
      stereotypes?.includes("sharp.ConceptAssociation") ||
      stereotypes?.includes("sharp.IdentifyingAssociation");
    if (isSharpAssoc) {
      // Contextual View suppress't cardinaliteit-symbolen — zie
      // CARDINALITY_SUPPRESSED_VIEW_KINDS hierboven. Data blijft
      // bestaan op het concept; alleen de render-laag laat 'm weg.
      if (viewKind && CARDINALITY_SUPPRESSED_VIEW_KINDS.has(viewKind)) {
        return { markerStart: undefined, markerEnd: undefined };
      }
      const sourceMarker = crowsFootMarkerFor(attributes?.source_cardinality);
      const targetMarker = crowsFootMarkerFor(attributes?.target_cardinality);
      const out: Partial<RelationStyle> = {};
      if (sourceMarker) out.markerStart = sourceMarker;
      if (targetMarker) out.markerEnd = targetMarker;
      return out;
    }
    // ArchiMate 3.2: an Association becomes directed by drawing a half-arrow
    // at the target end. Reverse direction is achieved by flipping the
    // relation itself (handled via the RelationTypePicker).
    if (attributes?.directed === true) {
      return { markerEnd: "ae-half-arrow" };
    }
    return {};
  }
  return {};
}

/**
 * De enum-waarden van `appearance` op een tekenlijn naar het relatietype
 * waarvan 'ie het uiterlijk leent. `own` staat er niet in: dat betekent "geen
 * lening", en dan valt de lijn terug op z'n eigen stijl.
 */
const APPEARANCE_TO_RELATION_TYPE: Readonly<Record<string, string>> = {
  composition: "archimate.Composition",
  aggregation: "archimate.Aggregation",
  assignment: "archimate.Assignment",
  realization: "archimate.Realization",
  serving: "archimate.Serving",
  access: "archimate.Access",
  triggering: "archimate.Triggering",
  flow: "archimate.Flow",
  influence: "archimate.Influence",
  specialization: "archimate.Specialization",
  association: "archimate.Association",
};

export function applyRelationStyle(
  edge: Edge,
  typeId: string,
  attributes?: Record<string, unknown>,
  stereotypes?: readonly string[],
  viewKind?: string,
): Edge {
  // Een vrije lijn mag het uiterlijk van een ArchiMate-relatietype lenen —
  // streepjes, pijlpunt, ruit — zonder er één te wórden. Puur weergave: in het
  // model blijft 'ie een `drawing.Line`, dus 'ie beweert nog steeds niets en
  // valt onder de matrixregel dat een tekenelement alleen tekenrelaties
  // draagt. Bedoeld voor legenda's: laten zien hoe een realisatie eruitziet
  // zonder er één te tekenen.
  const geleendUiterlijk =
    typeId.startsWith("drawing.") && typeof attributes?.appearance === "string"
      ? APPEARANCE_TO_RELATION_TYPE[attributes.appearance]
      : undefined;
  const stijlType = geleendUiterlijk ?? typeId;
  const base = styleForRelationType(stijlType);
  const overrides = attributeOverrides(stijlType, attributes, stereotypes, viewKind);
  const next: Edge = {
    ...edge,
    // Override-style wint van base zodat bv. een viewpoint-specifieke
    // stroke (paarse Sharp-CRUD-lijn) de ArchiMate-default kan
    // overrulen zonder een hele tweede RELATION_STYLES-tabel.
    style: { ...edge.style, ...base.style, ...(overrides.style ?? {}) },
  };
  // Pick markerStart / markerEnd in order: per-attribute override (which can
  // explicitly clear a marker by setting `undefined`), else the base style's
  // marker, else nothing.
  const markerStart = "markerStart" in overrides ? overrides.markerStart : base.markerStart;
  const markerEnd = "markerEnd" in overrides ? overrides.markerEnd : base.markerEnd;
  if (markerStart) next.markerStart = markerStart;
  if (markerEnd) next.markerEnd = markerEnd;
  return next;
}
